import React from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Visual-only preview of Subscription Options UI (no logic)
// Mirrors the right-side summary panel subscription cards without annual price line,
// and adds a VAT 20% line (for UA preview) beneath the "to pay" amount.
const plans = [
  {
    id: '360-days',
    title: '360 days (20% bonus)',
    toPay: '$4530',
    monthly: '$378',
    vat20: '$5436',
    savings: '$186',
    recommended: true
  },
  {
    id: '180-days',
    title: '180 days (10% bonus)',
    toPay: '$2307',
    monthly: '$384',
    vat20: '$2768',
    overpayment: '$58'
  },
  {
    id: '360-days-split',
    title: '360 days (split payment)',
    toPay: '$4716',
    monthly: '$393',
    vat20: '$5659',
    overpayment: '$186'
  }
];

export default function SubscriptionTestPage() {
  const { t } = useTranslation();
  const numericSx = { fontVariantNumeric: 'tabular-nums lining-nums', fontFeatureSettings: '"tnum" 1, "lnum" 1' };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f7f9f8',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      p: 3
    }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 420,
          mx: 'auto',
          bgcolor: 'white',
          borderRadius: 2,
          border: '1px solid rgba(0,0,0,0.12)',
          p: 3,
          position: 'sticky',
          top: 24
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2.5, fontWeight: 600, fontSize: 18 }}>
          {t('calculator.estimatedTotal', 'Орієнтовна сума')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h3" sx={{ color: '#00C853', fontWeight: 700, fontSize: 36 }}>
            $378
          </Typography>
          <Typography sx={{ fontSize: 16, color: '#00C853', ml: 0.5, alignSelf: 'flex-end', mb: 0.8 }}>
            {t('calculator.perMonthShort', '/mo')}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Subscription Options */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('calculator.subscriptionOptions', 'Subscription Options')}
          </Typography>

          {plans.map((plan) => (
            <Paper
              key={plan.id}
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'white',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {plan.title}
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', ...numericSx }}>
                    {plan.toPay} {t('subscription.toPay', 'to pay')}
                  </Typography>
                  {/* VAT 20% line (UA preview) */}
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, ...numericSx }}>
                    {t('subscription.vatIncluded', 'З ПДВ 20%: {{amount}}', { amount: plan.vat20 })}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#00C853', fontWeight: 600, ...numericSx }}>
                  {plan.monthly} {t('subscription.perMonth', '/month')}
                </Typography>
                {plan.savings && (
                  <Typography variant="caption" sx={{ color: '#00C853', display: 'block', mt: 0.25, ...numericSx }}>
                    {t('subscription.savings', 'Savings')} {plan.savings}
                  </Typography>
                )}
                {plan.overpayment && (
                  <Typography variant="caption" sx={{ color: '#f44336', display: 'block', mt: 0.25, ...numericSx }}>
                    +{plan.overpayment}
                  </Typography>
                )}
                {/* IMPORTANT: No annual green line here (removed per request) */}
              </Box>
            </Paper>
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary">
          UI preview only — static values, no logic.
        </Typography>
      </Paper>
    </Box>
  );
}
